import isPast from 'date-fns/isPast';
import isFuture from 'date-fns/isFuture';
import * as Yup from 'yup';
import pick from 'lodash/pick';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectAddressApiToken } from '@selectors/addressApi';

import { checkIsStreetNumber } from '@utils/profileForms';

import {
  INVALID_EMAIL_ERROR,
  REQUIRED_FIELD_ERROR,
} from '@common/constants/formValidation';
import { GENDER_FEMALE } from '@common/constants/genders';
import { DELIVERY_OPTIONS } from '@common/constants/giftCards';
import { requiredAddressSchema } from '@common/components/forms/AddressSection';

export const isValidDiscount = ({ discountPercentage, start, end }) => {
  const discountStartDate = new Date(start);
  const discountEndDate = new Date(end);

  return (
    !!discountPercentage &&
    isPast(discountStartDate) &&
    isFuture(discountEndDate)
  );
};

export const defaultValues = {
  address: {
    shippingAddress: {
      gender: GENDER_FEMALE,
      firstname: '',
      lastname: '',
      street: '',
      houseNr: '',
      additionalAddress: '',
      zipCode: '',
      city: '',
      country: '',
      email: '',
    },
    invoiceAddress: {
      gender: GENDER_FEMALE,
      firstname: '',
      lastname: '',
      street: '',
      houseNr: '',
      additionalAddress: '',
      zipCode: '',
      city: '',
      country: '',
      email: '',
    },
    isUserLogged: false,
    isSameAddressForInvoice: true,
  },
  directDelivery: null,
};

const getFilledProperties = obj =>
  Object.fromEntries(
    Object.entries(obj || {}).filter(
      ([, value]) => ![undefined, '', null].includes(value),
    ),
  );

export const useInitialValues = (
  addressRestoredValues,
  directDelivery,
  isUserLogged,
  { address: loggedUserAddress, ...loggedUserDetails } = {},
) => {
  const addressApiToken = useSelector(selectAddressApiToken);
  const [isStreetNumberAvailable, setIsStreetNumberAvailable] = useState(true);

  useEffect(() => {
    checkIsStreetNumber(loggedUserAddress, addressApiToken, {
      zipCode: 'zipCode',
      street: 'street',
    }).then(isStreetNumber => {
      setIsStreetNumberAvailable(isStreetNumber);
    });
  }, [loggedUserAddress, addressApiToken]);

  return useMemo(() => {
    const restoredShippingAddress = getFilledProperties(
      addressRestoredValues.shippingAddress,
    );
    const restoredInvoiceAddress = getFilledProperties(
      addressRestoredValues.invoiceAddress,
    );
    const loggedUserValues = isUserLogged
      ? pick({ ...loggedUserDetails, ...loggedUserAddress }, [
          'gender',
          'firstname',
          'lastname',
          'street',
          'houseNr',
          'additionalAddress',
          'zipCode',
          'city',
          'country',
        ])
      : {};

    loggedUserValues.noAdditionalStreetNumber = isStreetNumberAvailable;

    return {
      ...defaultValues,
      address: {
        ...defaultValues.address,
        ...addressRestoredValues,
        isUserLogged,
        shippingAddress: {
          ...defaultValues.address.shippingAddress,
          ...(directDelivery === DELIVERY_OPTIONS.TO_MY_HOME
            ? loggedUserValues
            : {}),
          ...restoredShippingAddress,
        },
        invoiceAddress: {
          ...defaultValues.address.invoiceAddress,
          ...(directDelivery === DELIVERY_OPTIONS.TO_THE_RECIPIENT
            ? loggedUserValues
            : {}),
          ...restoredInvoiceAddress,
        },
      },
      directDelivery,
    };
  }, [
    addressRestoredValues,
    defaultValues,
    directDelivery,
    isUserLogged,
    isStreetNumberAvailable,
  ]);
};

const validationSchemaHomeDelivery = Yup.object({
  shippingAddress: Yup.object().when('isUserLogged', {
    is: true,
    then: Yup.object(requiredAddressSchema),
    otherwise: Yup.object({
      ...requiredAddressSchema,
      email: Yup.string()
        .email(INVALID_EMAIL_ERROR)
        .required(REQUIRED_FIELD_ERROR),
    }),
  }),
  invoiceAddress: Yup.object().when('isSameAddressForInvoice', {
    is: false,
    then: Yup.object().shape(requiredAddressSchema),
  }),
});

const validationSchemaRecipientDelivery = Yup.object().shape({
  shippingAddress: Yup.object().shape(requiredAddressSchema),
  invoiceAddress: Yup.object().when('isUserLogged', {
    is: true,
    then: Yup.object(requiredAddressSchema),
    otherwise: Yup.object({
      ...requiredAddressSchema,
      email: Yup.string()
        .email(INVALID_EMAIL_ERROR)
        .required(REQUIRED_FIELD_ERROR),
    }),
  }),
});

export const validationSchema = Yup.object({
  address: Yup.object().when('directDelivery', {
    is: DELIVERY_OPTIONS.TO_MY_HOME,
    then: validationSchemaHomeDelivery,
    otherwise: validationSchemaRecipientDelivery,
  }),
  directDelivery: Yup.bool(),
});
